import React, { useState, useEffect } from 'react';
import { estatisticasService, ProducaoItem, ProducaoMensalResponse, EstatisticasService } from '../../services/estatisticasService';
import { formatCurrency } from '../../utils/formatters';

interface ProducaoCardProps {
  className?: string;
  mesAno?: string;
}

const ProducaoCard: React.FC<ProducaoCardProps> = ({ className = '', mesAno }) => {
  const [dados, setDados] = useState<ProducaoItem[]>([]);
  const [metaMensal, setMetaMensal] = useState<ProducaoMensalResponse['metaMensal'] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(true);

  const mesAnoAtual = mesAno || EstatisticasService.getMesAnoAtual();

  const carregarDados = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await estatisticasService.getProducaoMensal(mesAnoAtual);

      // O `apiClient.request` já retorna o body parseado. Dependendo da chamada
      // o shape pode ser: { success, metaMensal, data } OR { data: { metaMensal, data } }
      // Tratamos ambos os casos aqui para não depender de comportamento de wrapper.
      let meta: ProducaoMensalResponse['metaMensal'] | null = null;
      let items: ProducaoItem[] = [];

      // Caso 1: resposta no formato esperado diretamente
      if ((response as any)?.metaMensal) {
        meta = (response as any).metaMensal;
        items = (response as any).data || [];
      }

      // Caso 2: resposta encapsulada em `data` (ex.: apiClient antigo/axios)
      else if ((response as any)?.data && (response as any).data.metaMensal) {
        meta = (response as any).data.metaMensal;
        items = (response as any).data.data || [];
      }

      if (meta) {
        setMetaMensal(meta);
        setDados(items);
      } else {
        setError('Estrutura de dados inválida');
      }
    } catch (err) {
      setError('Erro ao carregar dados de produção');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    carregarDados();
  }, [mesAnoAtual]);

  if (loading) {
    return (
      <div className={`bg-white rounded-lg shadow-lg p-4 h-[450px] flex flex-col ${className}`}>
        <div className="animate-pulse">
          <div className="h-4 bg-gray-200 rounded w-1/3 mb-4"></div>
          <div className="space-y-3">
            <div className="h-3 bg-gray-200 rounded"></div>
            <div className="h-3 bg-gray-200 rounded w-5/6"></div>
            <div className="h-3 bg-gray-200 rounded w-4/6"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !metaMensal) {
    return (
      <div className={`bg-white rounded-lg shadow p-3 h-[450px] flex flex-col ${className}`}>
        <h2 className="text-sm font-semibold mb-3">📊 Produção</h2>
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center text-gray-500">
            <p className="text-xs">{error || 'Nenhuma meta encontrada'}</p>
            <button 
              onClick={carregarDados}
              className="mt-2 text-purple-600 hover:text-purple-800 text-xs"
            >
              Tentar novamente
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Calcular totais gerais - usar metaMensal para dados agregados
  const totalObjetivoPremio = metaMensal.valorPremio;
  const totalObjetivoComissao = metaMensal.valorComissao;
  const totalAteHojePremio = metaMensal.realizado.premioLiquido;
  const totalAteHojeComissao = metaMensal.realizado.comissao;
  const totalProducaoMes = metaMensal.realizado.quantidadeDocumentos;

  // Percentuais
  const percentualPremio = metaMensal.percentualAtingimento.premioLiquido;
  const percentualComissao = metaMensal.percentualAtingimento.comissao;

  // Produto AUTO
  const produtoAuto = dados.find(item => item.produto === 'AUTO');

  // Média diária necessária (usando o primeiro item como referência)
  const necessarioDiario = dados.length > 0 ? dados[0].necessarioDiario : null;

  const getCorProgresso = (percentual: number): string => {
    // Sempre usar roxo escuro para os valores percentuais, conforme solicitado
    return 'text-purple-800';
  };

  const getCorBarraProgresso = (percentual: number): string => {
    if (percentual >= 100) return 'bg-purple-500';
    if (percentual >= 80) return 'bg-purple-400';
    if (percentual >= 60) return 'bg-purple-300';
    return 'bg-purple-200';
  };

  return (
    <div className={`bg-white rounded-lg shadow-lg p-4 ${expanded ? 'h-[450px] overflow-y-auto' : 'h-auto'} flex flex-col ${className}`}>
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
          Produção
          <span className="text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded-full font-semibold">
            {mesAnoAtual}
          </span>
        </h2>
        <div className="flex gap-2">
          <button 
            onClick={() => setExpanded(!expanded)}
            className="p-1 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded transition-colors"
            title={expanded ? 'Recolher' : 'Expandir'}
          >
            <svg 
              className={`w-4 h-4 transition-transform ${expanded ? 'rotate-180' : ''}`} 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
        </div>
      </div>

      {expanded && (
        <>
          {/* Progresso Geral */}
          <div className="mb-4">
            <div className="flex justify-between items-center mb-1">
              <span className="text-xs font-medium text-gray-800">Progresso Prêmio</span>
              <span className={`text-xs font-bold ${getCorProgresso(percentualPremio)}`}>
                {percentualPremio.toFixed(1)}%
              </span>
            </div>
            
            <div className="w-full bg-gray-200 rounded-full h-2 mb-1">
              <div 
                className={`h-2 rounded-full transition-all duration-300 ${getCorBarraProgresso(percentualPremio)}`}
                style={{ width: `${Math.min(percentualPremio, 100)}%` }}
              ></div>
            </div>
            
            <div className="flex justify-between items-center mb-1 mt-2">
              <span className="text-xs font-medium text-gray-800">Progresso Comissão</span>
              <span className={`text-xs font-bold ${getCorProgresso(percentualComissao)}`}>
                {percentualComissao.toFixed(1)}%
              </span>
            </div>
            
            <div className="w-full bg-gray-200 rounded-full h-2 mb-1">
              <div 
                className={`h-2 rounded-full transition-all duration-300 ${getCorBarraProgresso(percentualComissao)}`}
                style={{ width: `${Math.min(percentualComissao, 100)}%` }}
              ></div>
            </div>
            
            {necessarioDiario && necessarioDiario.diasRestantes > 0 && (
              <div className="flex justify-between text-xs text-gray-700 mt-1">
                <span>{necessarioDiario.diasRestantes}d restantes</span>
              </div>
            )}
          </div>

          {/* Métricas Principais */}
          <div className="grid grid-cols-2 gap-2 mb-3">
            <div>
              <p className="text-xs text-gray-700">Prêmio Líquido</p>
              <p className="text-sm font-semibold">{formatCurrency(totalAteHojePremio)}</p>
            </div>
            <div>
              <p className="text-xs text-gray-700">Meta Prêmio</p>
              <p className="text-sm font-semibold text-blue-600">{formatCurrency(totalObjetivoPremio)}</p>
            </div>
            <div>
              <p className="text-xs text-gray-700">Comissão</p>
              <p className="text-sm font-semibold">{formatCurrency(totalAteHojeComissao)}</p>
            </div>
            <div>
              <p className="text-xs text-gray-700">Meta Comissão</p>
              <p className="text-sm font-semibold text-green-600">{formatCurrency(totalObjetivoComissao)}</p>
            </div>
          </div>

          {/* Valor médio para alcançar objetivo */}
          {necessarioDiario && (
            <div className="mb-3 p-2 bg-yellow-50 rounded border">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-xs text-yellow-700 font-medium">Valor médio diário necessário:</span>
              </div>
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div>
                  <span className="text-yellow-700">Prêmio: </span>
                  <span className="font-bold text-yellow-900">
                    {formatCurrency(necessarioDiario.premioLiquido)}
                  </span>
                </div>
                <div>
                  <span className="text-yellow-700">Comissão: </span>
                  <span className="font-bold text-yellow-900">
                    {formatCurrency(necessarioDiario.comissao)}
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Produto a Produto */}
          <div className="mb-3">
            <h3 className="text-xs font-medium text-gray-800 mb-2">Por Produto</h3>
            {produtoAuto && (
              <div className="mb-2 p-3 bg-purple-50 rounded border border-purple-200">
                <div className="text-sm font-semibold text-purple-800 mb-2">AUTO</div>
                <div className="grid grid-cols-2 gap-2 text-xs font-medium text-purple-800 mb-1">
                  <div>Prêmio até agora</div>
                  <div>Objetivo</div>
                </div>
                <div className="grid grid-cols-2 gap-2 text-xs font-bold text-purple-900 mb-2">
                  <div>{formatCurrency(produtoAuto.realizadoInicioVigencia.totalPremioLiquido)}</div>
                  <div>{formatCurrency(produtoAuto.objetivoMensal.premioLiquido)}</div>
                </div>
                <div className="text-xs mb-2">
                  <span className="text-purple-800">Quantidade: </span>
                  <span className="font-bold text-purple-900">
                    {produtoAuto.realizadoInicioVigencia.quantidadeDocumentos}
                  </span>
                </div>
                <div className="border-t border-purple-200 pt-2">
                  <div className="text-xs font-medium text-purple-800 mb-1">Renovações</div>
                  <div className="flex gap-4 text-xs">
                    <div>
                      <span className="text-purple-800">Quantidade: </span>
                      <span className="font-bold text-purple-900">
                        {produtoAuto.realizadoFimVigencia.quantidadeDocumentos}
                      </span>
                    </div>
                    <div>
                      <span className="text-purple-800">Valor a Renovar: </span>
                      <span className="font-bold text-purple-900">
                        {formatCurrency(produtoAuto.realizadoFimVigencia.totalPremioLiquido)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            )}
            {dados.filter(item => item.produto !== 'AUTO').map((item, index) => (
              <div key={index} className="mb-2 p-3 bg-gray-50 rounded border">
                <div className="text-sm font-semibold text-gray-800 mb-2">{item.produto}</div>
                <div className="grid grid-cols-2 gap-2 text-xs font-medium text-gray-800 mb-1">
                  <div>Prêmio até agora</div>
                  <div>Objetivo</div>
                </div>
                <div className="grid grid-cols-2 gap-2 text-xs font-bold text-gray-900 mb-2">
                  <div>{formatCurrency(item.realizadoInicioVigencia.totalPremioLiquido)}</div>
                  <div>{formatCurrency(item.objetivoMensal.premioLiquido)}</div>
                </div>
                <div className="text-xs mb-2">
                  <span className="text-gray-800">Quantidade: </span>
                  <span className="font-bold text-gray-900">
                    {item.realizadoInicioVigencia.quantidadeDocumentos}
                  </span>
                </div>
                <div className="border-t border-gray-200 pt-2">
                  <div className="text-xs font-medium text-gray-800 mb-1">Renovações</div>
                  <div className="flex gap-4 text-xs">
                    <div>
                      <span className="text-gray-800">Quantidade: </span>
                      <span className="font-bold text-gray-900">
                        {item.realizadoFimVigencia.quantidadeDocumentos}
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-800">Valor a Renovar: </span>
                      <span className="font-bold text-gray-900">
                        {formatCurrency(item.realizadoFimVigencia.totalPremioLiquido)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Atualizado */}
          <div className="mt-auto pt-2 border-t">
            <div className="text-xs text-gray-500">
              Atualizado: {new Date().toLocaleDateString('pt-BR')}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default ProducaoCard;
